/**
 * Editor modelli email (oggetto + corpo) per tipo documento.
 *
 * Caricata da Impostazioni Azienda > tab "Modelli email".
 * Legge/scrive tramite l'API config admin generica, la STESSA usata da
 * ConfigurazioneAdmin.tsx:
 *   - GET  /api/v1/admin/configurazione?modulo=email  (lista chiavi modulo email)
 *   - PUT  /api/v1/admin/configurazione/{chiave}       body {valore: <json stringificato>}
 *   - POST /api/v1/admin/configurazione/{chiave}/reset (ripristina valore_default)
 *
 * Ogni chiave e' tipo='json', colonna `valore` di tipo Text contenente una
 * stringa JSON `{"oggetto": "...", "corpo": "..."}` (v. migration
 * core/migrations/versions/0026_seed_email_templates.py e
 * core/app/models/structural.py ConfigurazioneTenant.valore: Mapped[str]).
 *
 * Tipi documento noti (T3, migration 0026): struttura ESTENSIBILE, basta
 * aggiungere una voce a TIPI_DOCUMENTO per un nuovo tipo (es. fattura in un
 * cantiere futuro, fase D del piano).
 *
 * LEGENDA segnaposto: scelta deliberata, estratta via regex dai token
 * {{...}} presenti nel campo `descrizione` della chiave, letto dall'API a
 * runtime - MAI hardcodata in questo file. Il piano T4 nota esplicitamente
 * che un fixer concorrente sta aggiungendo {{data}} e {{scadenza_risposta}}
 * al set di segnaposto: leggendo la legenda dalla descrizione (fonte di
 * verita' nel DB, seminata dalla migration), il componente si aggiorna da
 * solo quando la migration di seed cambia, senza bisogno di un nuovo
 * deploy frontend. La descrizione completa e' mostrata anche per esteso
 * sotto la legenda, perche' contiene note semantiche utili (es. "nota =
 * messaggio opzionale dell'operatore, riga omessa se assente").
 *
 * ANTEPRIMA: client-side, sostituzione pura {{token}} -> valore di esempio
 * fisso (dizionario ESEMPI). Segnaposto senza esempio noto restano visibili
 * cosi' come sono (nessun dato inventato, nessun errore silente). Non
 * replica la logica di "omissione riga se il valore e' vuoto" del motore di
 * invio reale (ordini/backend/services.py _omit_riga_se_vuoto): qui i
 * valori di esempio sono sempre non vuoti, quindi l'anteprima e'
 * un'approssimazione ragionevole del rendering finale, non uno specchio
 * esatto - accettabile per un editor di modelli, non per la conferma di
 * invio (quella sara' T5, con endpoint /preventivi/{id}/anteprima-email).
 *
 * Solo admin (pagina gia' gated da ImpostazioniAzienda/routing di sistema).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface ConfigItem {
  id: string;
  chiave: string;
  valore: string;
  tipo: string;
  modulo: string;
  descrizione: string;
  valore_default: string | null;
  ordinamento: number;
  sola_lettura: boolean;
  riservato: boolean;
}

interface TemplateEmail {
  oggetto: string;
  corpo: string;
}

interface TipoDocumento {
  chiave: string;
  label: string;
}

/** Struttura estensibile: un nuovo tipo documento (es. fattura) richiede solo una nuova voce qui + la relativa chiave seminata lato backend. */
const TIPI_DOCUMENTO: TipoDocumento[] = [
  { chiave: 'email.template_preventivo_vendita', label: 'Preventivo cliente' },
  { chiave: 'email.template_rfq_fornitore', label: 'Richiesta preventivo fornitore' },
];

/**
 * Valori di esempio per l'anteprima client-side. Segnaposto non presenti
 * qui restano visibili come {{token}} nell'anteprima: mai inventare un
 * valore per un segnaposto sconosciuto.
 */
const ESEMPI: Record<string, string> = {
  numero: 'PV-2026-00123',
  cliente: 'Mario Rossi S.r.l.',
  fornitore: 'Fornitore Esempio S.p.A.',
  totale: '1.234,56',
  valuta: 'EUR',
  validita: '31/07/2026',
  modalita_pagamento: 'Bonifico 30gg d.f.',
  nota: 'Grazie per la fiducia accordataci.',
  righe: '- Articolo esempio A x 10 pz\n- Articolo esempio B x 5 mc',
  data: '19/07/2026',
  scadenza_risposta: '26/07/2026',
};

const parseTemplate = (valore: string): TemplateEmail => {
  try {
    const parsed = JSON.parse(valore);
    return {
      oggetto: typeof parsed.oggetto === 'string' ? parsed.oggetto : '',
      corpo: typeof parsed.corpo === 'string' ? parsed.corpo : '',
    };
  } catch {
    return { oggetto: '', corpo: '' };
  }
};

/** Estrae i segnaposto {{token}} citati nella descrizione (fonte di verita' DB), deduplicati, in ordine di comparsa. */
const estraiSegnaposto = (descrizione: string): string[] => {
  const matches = descrizione.match(/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g) ?? [];
  return [...new Set(matches)];
};

/** Sostituzione pura {{token}} -> esempio. Nessun eval/Jinja2. Token ignoti restano visibili invariati. */
const renderAnteprima = (testo: string): string =>
  testo.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (match, token: string) =>
    Object.prototype.hasOwnProperty.call(ESEMPI, token) ? ESEMPI[token] : match
  );

/* ------------------------------------------------------------------ */
/* Sotto-componente: card per un singolo tipo documento               */
/* ------------------------------------------------------------------ */

interface TemplateCardProps {
  tipo: TipoDocumento;
  item: ConfigItem | undefined;
  token: string;
  onSaved: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ tipo, item, token, onSaved }) => {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [draft, setDraft] = useState<TemplateEmail>({ oggetto: '', corpo: '' });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAnteprima, setShowAnteprima] = useState(false);

  useEffect(() => {
    if (item) setDraft(parseTemplate(item.valore));
  }, [item]);

  if (!item) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{tipo.label}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            Modello non configurato per questo tenant (chiave <code>{tipo.chiave}</code> assente).
            Contattare l&apos;assistenza per la migrazione di inizializzazione.
          </p>
        </CardContent>
      </Card>
    );
  }

  const originale = parseTemplate(item.valore);
  const isModified = draft.oggetto !== originale.oggetto || draft.corpo !== originale.corpo;
  const segnaposto = estraiSegnaposto(item.descrizione);
  const isDefault = item.valore_default !== null && item.valore === item.valore_default;

  const salva = async () => {
    const oggettoTrim = draft.oggetto.trim();
    const corpoTrim = draft.corpo.trim();
    if (!oggettoTrim) {
      setError('Oggetto obbligatorio');
      return;
    }
    if (!corpoTrim) {
      setError('Corpo obbligatorio');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const valoreJson = JSON.stringify({ oggetto: oggettoTrim, corpo: draft.corpo });
      const res = await fetch(`/api/v1/admin/configurazione/${tipo.chiave}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ valore: valoreJson }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Modello salvato');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const ripristina = async () => {
    if (!window.confirm(`Ripristinare il modello "${tipo.label}" al testo di default? Le modifiche non salvate andranno perse.`)) return;
    setResetting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/admin/configurazione/${tipo.chiave}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Modello ripristinato al default');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore ripristino');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{tipo.label}</span>
          {isDefault && <Badge variant="outline">default</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: 'var(--color-success, #22c55e)', color: 'var(--color-success, #22c55e)' }}>
            {success}
          </div>
        )}

        {segnaposto.length > 0 && (
          <div className="rounded-md border p-3 space-y-1" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-muted-foreground)' }}>
              Segnaposto disponibili
            </p>
            <div className="flex flex-wrap gap-1.5">
              {segnaposto.map((s) => (
                <Badge key={s} variant="secondary" className="font-mono text-xs">{s}</Badge>
              ))}
            </div>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
              {item.descrizione}
            </p>
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-sm">Oggetto</Label>
          <Input
            value={draft.oggetto}
            onChange={(e) => setDraft((d) => ({ ...d, oggetto: e.target.value }))}
            placeholder="Oggetto dell'email"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-sm">Corpo</Label>
          <textarea
            value={draft.corpo}
            onChange={(e) => setDraft((d) => ({ ...d, corpo: e.target.value }))}
            rows={10}
            className="w-full rounded-md border p-2 text-sm"
            style={{
              borderColor: 'var(--color-border, #e5e7eb)',
              backgroundColor: 'var(--color-background, white)',
              color: 'var(--color-foreground, inherit)',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            }}
            placeholder="Corpo dell'email, usa i segnaposto {{...}} sopra elencati"
          />
        </div>

        {showAnteprima && (
          <div className="rounded-md border p-3 space-y-2" style={{ borderColor: 'var(--color-primary, #6366f1)' }}>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-muted-foreground)' }}>
              Anteprima (dati di esempio, segnaposto sconosciuti restano visibili)
            </p>
            <div className="text-sm">
              <span className="font-medium">Oggetto: </span>
              <span>{renderAnteprima(draft.oggetto) || '(vuoto)'}</span>
            </div>
            <pre
              className="whitespace-pre-wrap text-sm"
              style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}
            >
              {renderAnteprima(draft.corpo) || '(vuoto)'}
            </pre>
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <Button variant="outline" size="sm" onClick={() => setShowAnteprima((s) => !s)}>
            {showAnteprima ? 'Nascondi anteprima' : 'Anteprima'}
          </Button>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void ripristina()}
              disabled={resetting || item.valore_default === null || isDefault}
              style={{ color: 'var(--color-destructive)' }}
            >
              {resetting ? 'Ripristino...' : 'Ripristina default'}
            </Button>
            <Button size="sm" onClick={() => void salva()} disabled={saving || !isModified}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Componente principale                                              */
/* ------------------------------------------------------------------ */

export const ModelliEmailEditor: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';
  const [items, setItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/configurazione?modulo=email', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: ConfigItem[] = await res.json();
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void carica(); }, [carica]);

  if (loading) return <div className="p-4">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
          {error}
        </div>
      )}
      <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
        Modelli di oggetto e corpo per le email inviate dal modulo Ordini (preventivi/RFQ).
        I segnaposto tra doppie graffe vengono sostituiti con i dati reali del documento all&apos;invio.
        Il PDF resta sempre allegato automaticamente.
      </p>
      <div className="space-y-4">
        {TIPI_DOCUMENTO.map((tipo) => (
          <TemplateCard
            key={tipo.chiave}
            tipo={tipo}
            item={items.find((i) => i.chiave === tipo.chiave)}
            token={token}
            onSaved={() => void carica()}
          />
        ))}
      </div>
    </div>
  );
};

export default ModelliEmailEditor;
